/* eslint-disable no-restricted-imports */
import React, {useEffect, useMemo} from 'react'
import {UIGameActionButton} from '../../../../common/UIGameActionButton/index.ts'
import {useActionButtonCommonErrors} from '../../utils.ts'
import {KNIGHT_MOVE_IMAGE_PATH} from './utils.ts'
import {T} from '@colonist/ui'
import {ActionState, TurnState} from '@colonist/game-states'
import {useGameStore} from '../../../../store/GameStoreContext.ts'
import {type ActionStatus} from '@colonist/game-base-types'
import {type TranslationObject} from '@colonist/i18n'
import {useUIGameSocketContext} from '../../../../contexts/UIGameSocketContext.tsx'
import {useCurrentStateValidator} from '../../../../store/selectors/useCurrentStateValidator.ts'
import {useMechanicValidatorKnightActions} from '../../../../store/selectors/useMechanicValidatorKnightActions.ts'

export const UIGameActionButtonKnightMove = ({
    className,
    onDisabledChange,
}: {
    onDisabledChange: (isDisabled: boolean) => void
    className?: string
}) => {
    const myColor = useGameStore((state) => state.myColor)
    const currentState = useGameStore((state) => state.gameState.currentState)
    const currentStateValidator = useCurrentStateValidator()
    const {errorHelperText, hasCommonErrors} = useActionButtonCommonErrors()
    const {sendTakeKnightAction, sendClearTileHighlight, sendClearCornerHighlight, sendCancelAction} = useUIGameSocketContext()
    const isSpecialBuildPhase = currentState.turnState == TurnState.SpecialBuildPhase
    const knightActionsValidator = useMechanicValidatorKnightActions()

    const {actionStatus, knightLocationsEligibleForActionsCount}: {
        actionStatus: ActionStatus
        knightLocationsEligibleForActionsCount: number
    } = useMemo(() => {
        if(!knightActionsValidator) {
            return {actionStatus: {canTakeAction: false}, knightLocationsEligibleForActionsCount: 0}
        }

        const knightLocationsEligibleForActionsCountLocal = knightActionsValidator.getAllKnightLocationsEligibleForActions(myColor).length
        const canActivateKnightActionStatus = knightActionsValidator.canPlayerTakeKnightAction(myColor)
        return {
            actionStatus: canActivateKnightActionStatus,
            knightLocationsEligibleForActionsCount: knightLocationsEligibleForActionsCountLocal,
        }
    }, [knightActionsValidator, myColor])

    const disabled = hasCommonErrors || !actionStatus.canTakeAction || isSpecialBuildPhase
    const actionState = currentStateValidator.getPlayerActionState(myColor)
    const isMovingKnight = [ActionState.SelectKnightToTakeAction, ActionState.SelectKnightAction].includes(actionState) && !disabled
    const icon = KNIGHT_MOVE_IMAGE_PATH[myColor]
    const helperText: TranslationObject | undefined = errorHelperText || actionStatus.errorMessageIfCantTakeAction

    useEffect(() => {
        onDisabledChange(disabled)
    }, [disabled])

    const onClick = () => {
        if(disabled) return
        if(isMovingKnight) {
            sendClearCornerHighlight()
            sendClearTileHighlight()
            sendCancelAction()
        } else {
            sendTakeKnightAction()
        }
    }

    return <UIGameActionButton
        className={className}
        count={knightLocationsEligibleForActionsCount}
        countColor={myColor}
        disabled={disabled}
        helperText={helperText}
        highlight={isMovingKnight}
        iconPath={icon}
        tooltipTitle={<T id='strings:game.tooltips.knightAction'/>}
        onClick={onClick}
    />
}
